import { Box, Button, Paper } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { CarrierVisitDirection } from '@planning/app/api'
import { useRailVisitBreadcrumbs } from '@planning/hooks/useRailVisitBreadcrumbs'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, Header, useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { Helmet } from 'react-helmet-async'
import { useLocation, useNavigate, useParams } from 'react-router'
import { AddEmptyRailcarPageForm } from './Components/Forms/AddEmptyRailcarPageForm'

export const AddEmptyRailcarPage = observer(() => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const location = useLocation()
  const navigate = useNavigate()

  const { railVisitItemStore, railVisitQueryService } = usePlanningStore()
  const { visitId, direction } = useParams<{ visitId: string; direction: string }>()
  const railVisitId = Number(visitId)

  useAsyncFetch(async () => {
    if (!railVisitId) return
    await railVisitQueryService.fetchById(railVisitId)
  }, [railVisitId, railVisitQueryService])

  const railVisitItem = computed(() => railVisitItemStore.elements[railVisitId]).get()
  const { breadcrumbs } = useRailVisitBreadcrumbs(railVisitItem)

  const returnPath = location.state?.returnPath || `/rail-visits/${railVisitId}/${direction}`
  const title = t('addEmptyRailcar', 'Add empty railcar')

  const visitDirection =
    direction === 'discharge' ? CarrierVisitDirection.Inbound : CarrierVisitDirection.Outbound

  return (
    <Box sx={{ backgroundColor: theme.palette.grey[200], minHeight: '100vh' }}>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <Breadcrumbs items={breadcrumbs} />

      <Header
        title={title}
        rightRenderOption={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button color='inherit' onClick={() => navigate(returnPath)} variant='text'>
              {t('cancel', 'Cancel')}
            </Button>
            <Button type='submit' form='add-empty-railcar-form' color='primary' variant='contained'>
              {t('save', 'Save')}
            </Button>
          </Box>
        }
      />

      <Box sx={{ p: 3 }}>
        {railVisitItem && (
          <Paper sx={{ p: 2, borderRadius: 1, border: `1px solid ${theme.palette.grey[300]}` }}>
            <AddEmptyRailcarPageForm
              direction={visitDirection}
              railVisit={railVisitItem.data}
              onSuccess={() => navigate(returnPath)}
            />
          </Paper>
        )}
      </Box>
    </Box>
  )
})
